<template>
  <AnnDialog
    :model-value="props.modelValue"
    @update:modelValue="emit('update:modelValue', $event)"
  >
    <div class="ann-reward-history">
      <img
        class="ann-reward-history-bg"
        :src="backgroundImg"
        alt=""
        aria-hidden="true"
      />

      <div class="ann-reward-history-title">
        <img
          :class="`is-${titleLang}`"
          :src="titleImg"
          :alt="t('ann2026.rewardHistory.title')"
        />
      </div>

      <div class="ann-reward-history-header ann-reward-history-header--reward">
        {{ t('ann2026.rewardHistory.reward') }}
      </div>
      <div class="ann-reward-history-header ann-reward-history-header--received">
        {{ t('ann2026.rewardHistory.receivedAt') }}
      </div>
      <img
        class="ann-reward-history-divider"
        :src="dividerImg"
        alt=""
        aria-hidden="true"
      />

      <!-- 常驻左下角装饰，不是空状态专属；文件名 empty- 为迁移历史遗留。 -->
      <img
        class="ann-reward-history-empty-character"
        :src="emptyCharacterImg"
        alt=""
        aria-hidden="true"
      />

      <div
        v-if="displayRecords.length"
        class="ann-reward-history-scroll"
      >
        <div
          v-for="row in displayRecords"
          :key="row.key"
          class="ann-reward-history-row"
        >
          <div class="ann-reward-history-name">
            <span>{{ row.name }}</span>
            <span>×{{ row.count }}</span>
          </div>
          <div class="ann-reward-history-time">{{ row.time }}</div>
        </div>
      </div>

      <template v-else>
        <img
          class="ann-reward-history-empty-icon"
          :src="emptyIconImg"
          alt=""
          aria-hidden="true"
        />
      </template>

      <AnnDialogClose
        :aria-label="t('ann2026.dialog.close')"
        @close="emit('update:modelValue', false)"
      />
    </div>
  </AnnDialog>
</template>

<script setup>
import { computed, defineEmits, defineProps } from 'vue'
import { useI18n } from 'vue-i18n'
import AnnDialog from '../AnnDialog/index.vue'
import AnnDialogClose from '../AnnDialogClose/index.vue'
import {
  formatTime,
  getItemName,
  getRewardCount,
  getRewardId,
  parseProps,
} from '../../utils/reward'
import backgroundImg from '@/assets/imgs/act/ann2026/popups/reward-history/background.png'
import dividerImg from '@/assets/imgs/act/ann2026/popups/reward-history/divider.png'
import emptyCharacterImg from '@/assets/imgs/act/ann2026/popups/reward-history/empty-character.png'
import emptyIconImg from '@/assets/imgs/act/ann2026/popups/reward-history/empty-icon.png'
import titleEn from '@/assets/imgs/act/ann2026/popups/reward-history/title/en.png'
import titleZh from '@/assets/imgs/act/ann2026/popups/reward-history/title/zh.png'
import titleVi from '@/assets/imgs/act/ann2026/popups/reward-history/title/vi.png'
import titleId from '@/assets/imgs/act/ann2026/popups/reward-history/title/id.png'
import titleTh from '@/assets/imgs/act/ann2026/popups/reward-history/title/th.png'

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false,
  },
  records: {
    type: Array,
    default: () => [],
  },
})

const emit = defineEmits(['update:modelValue'])

const { t, te, locale } = useI18n({ useScope: 'global' })

const titleLang = computed(() => {
  if (locale.value === 'vi') return 'vi'
  if (locale.value === 'id') return 'id'
  if (locale.value === 'th') return 'th'
  if (locale.value === 'hk' || locale.value === 'tw' || locale.value === 'cn') {
    return 'zh'
  }
  return 'en'
})

const titleImg = computed(() => {
  if (titleLang.value === 'vi') return titleVi
  if (titleLang.value === 'id') return titleId
  if (titleLang.value === 'th') return titleTh
  if (titleLang.value === 'zh') return titleZh
  return titleEn
})

const recordTime = (record) =>
  formatTime(
    record.receiveTime ??
      record.receivedAt ??
      record.createTime ??
      record.rewardTime,
    locale.value,
  )

const displayRecords = computed(() =>
  props.records.flatMap((record, recordIndex) => {
    const snapshot = parseProps(record.propSnapshot)
    const fireCount = Number(record.fireCount || 0)
    const time = recordTime(record)

    if (!snapshot.length && fireCount > 0) {
      return [
        {
          key: `fire-${time}-${recordIndex}`,
          name: t('ann2026.flamePower'),
          count: fireCount,
          time,
        },
      ]
    }

    const rewards = snapshot.length ? snapshot : [record]
    return rewards
      .map((reward, rewardIndex) => {
        const id = getRewardId(reward)
        const name =
          getItemName(id, t, te) ||
          reward.name ||
          reward.itemName ||
          reward.rewardName ||
          ''

        if (!name) return null

        return {
          key: `${id || 'reward'}-${time}-${recordIndex}-${rewardIndex}`,
          name,
          count: getRewardCount(reward),
          time,
        }
      })
      .filter(Boolean)
  }),
)
</script>

<style lang="less" scoped>
.ann-reward-history {
  position: relative;
  width: 701px;
  height: 1001px;
  overflow: visible;
}

.ann-reward-history-bg {
  position: absolute;
  top: 0;
  left: 0;
  width: 701px;
  height: 1001px;
  pointer-events: none;
}

.ann-reward-history-title {
  position: absolute;
  // 设计意图：标题向上探出弹窗顶部边缘。
  top: -10px;
  left: 0;
  z-index: 3;
  width: 701px;
  display: flex;
  justify-content: center;

  img {
    display: block;
    // 图片标题锁宽跟随缩放，height auto 保持各语言切图等比不变形。
    height: auto;
    object-fit: contain;

    &.is-en {
      width: 529px;
    }

    &.is-zh {
      width: 327px;
    }

    // TODO: 越南语标题设计宽 731px，当前先限制为弹窗宽度，待设计师确认。
    &.is-vi {
      width: 731px;
      max-width: 100%;
    }

    &.is-id {
      width: 558px;
    }

    &.is-th {
      width: 560px;
    }
  }
}

.ann-reward-history-header {
  position: absolute;
  top: 130px;
  z-index: 2;
  height: 20px;
  color: #f44382;
  font-size: 20px;
  line-height: 20px;
  font-weight: 700;
  white-space: nowrap;

  &--reward {
    left: 104px;
    width: 220px;
  }

  &--received {
    left: 394px;
    width: 207px;
  }
}

.ann-reward-history-divider {
  position: absolute;
  top: 178.5px;
  left: 100.5px;
  z-index: 2;
  width: 491px;
  height: 2px;
}

.ann-reward-history-scroll {
  position: absolute;
  top: 200px;
  left: 0;
  z-index: 2;
  width: 701px;
  height: 690px;
  padding-top: 29px;
  box-sizing: border-box;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
}

.ann-reward-history-row {
  display: flex;
  align-items: flex-start;
  width: 701px;
  min-height: 41px;
  margin: 0;
  padding: 0 100px 12px 106px;
  box-sizing: border-box;
  color: #6845bb;
  font-size: 18px;
  line-height: 26px;
  font-weight: 700;
}

.ann-reward-history-name {
  flex: 0 0 256px;
  width: 256px;
  word-break: break-word;

  span + span {
    margin-left: 6px;
  }
}

.ann-reward-history-time {
  flex: 0 0 208px;
  width: 208px;
  margin-left: 31px; // 对齐表头领取时间列：393 - 106 - 256
  white-space: nowrap;
}

.ann-reward-history-empty-character {
  position: absolute;
  top: 11rem;
  left: -16px;
  z-index: 3;
  width: 100px;
  height: auto;
  pointer-events: none;
}

.ann-reward-history-empty-icon {
  position: absolute;
  top: 757px;
  left: 143px;
  z-index: 4;
  width: 66px;
  height: 68px;
  pointer-events: none;
}
</style>
